import { Component, OnInit, OnDestroy, inject, computed, ViewChild, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { DashboardService } from '../../core/services/dashboard.service';
import { AcresAdvantageService, AcresAdvantageDashboard } from '../../core/services/acres-advantage.service';
import { OrganizationContextService } from '../../core/services/organization-context.service';
import { User } from '../../core/models/user.model';
import {
  OrganizationActivity,
  getActivityIcon,
  getResourceRoute
} from '../../core/models/organization-activity.model';
import { UnifiedOnboardingComponent } from '../../shared/components/unified-onboarding/unified-onboarding.component';
import { QuickScheduleModalComponent } from '../../shared/components/quick-schedule-modal/quick-schedule-modal.component';
import { APP_STORE_URLS } from '../../shared/constants/app_store_urls';

/**
 * Dashboard Page Component
 * Main landing page after authentication
 * Based on: agent-os/design/mockups/web-portal/dashboard-*.png
 *
 * Reference: /agent-os/specs/2026-01-16-dashboard-updates/spec.md
 * Reference: /agent-os/specs/2026-02-13-unified-onboarding-redesign/spec.md
 */
@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    UnifiedOnboardingComponent,
    QuickScheduleModalComponent
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private dashboardService = inject(DashboardService);
  private acresAdvantageService = inject(AcresAdvantageService);
  private organizationContext = inject(OrganizationContextService);
  private router = inject(Router);

  @ViewChild('quickScheduleModal') quickScheduleModal!: QuickScheduleModalComponent;

  currentUser: User | null = null;

  // App store links for the dashboard's CropSwipe download section.
  readonly appStoreUrls = APP_STORE_URLS;

  // Loading state for initial load
  isInitializing = signal(true);

  // Acres Advantage points data
  acresAdvantageData = signal<AcresAdvantageDashboard | null>(null);
  isLoadingAcresAdvantage = signal(false);

  // Computed stats from DashboardService
  readonly stats = computed(() => {
    const s = this.dashboardService.stats$();
    return {
      totalDrones: s?.totalDrones ?? 0,
      activeDrones: s?.activeDrones ?? 0,
      flightHours: s?.flightHours ?? 0,
      totalAcresSprayed: s?.totalAcresSprayed ?? 0,
      upcomingFlights: s?.upcomingFlights ?? 0
    };
  });

  // Activities from DashboardService (limited to 3 for dashboard)
  readonly activities = computed(() => this.dashboardService.recentActivities$());
  readonly isLoadingActivities = computed(() => this.dashboardService.isLoadingActivities$());
  readonly isLoadingStats = computed(() => this.dashboardService.isLoadingStats$());

  // Helper to check if activity is new (for animation)
  readonly newActivityIds = computed(() => this.dashboardService.newActivityIds$());

  async ngOnInit(): Promise<void> {
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });

    // Initialize dashboard data and Acres Advantage in parallel
    await Promise.all([
      this.dashboardService.initialize(),
      this.loadAcresAdvantageData()
    ]);
    this.isInitializing.set(false);
  }

  /**
   * Load Acres Advantage summary data for the points snapshot card
   */
  private async loadAcresAdvantageData(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    this.isLoadingAcresAdvantage.set(true);
    try {
      const data = await this.acresAdvantageService.getDashboardData(orgId).toPromise();
      if (data) {
        this.acresAdvantageData.set(data);
      }
    } catch {
      // Silently fail — the Acres Advantage card just won't show
    } finally {
      this.isLoadingAcresAdvantage.set(false);
    }
  }

  /**
   * Format a number with commas for display
   */
  formatNumber(value: number): string {
    return value.toLocaleString();
  }

  ngOnDestroy(): void {
    // DashboardService handles its own cleanup via ngOnDestroy
  }

  getGreeting(): string {
    return 'Hello';
  }

  getUserFirstName(): string {
    if (!this.currentUser?.displayName && !this.currentUser?.email) {
      return 'User';
    }

    const displayName = this.currentUser.displayName || this.currentUser.email?.split('@')[0] || 'User';
    const firstName = displayName.split(' ')[0];

    // Capitalize first name
    return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  }

  /**
   * Get the icon class for an activity based on its event type
   */
  getActivityIconClass(activity: OrganizationActivity): string {
    return getActivityIcon(activity.eventType);
  }

  /**
   * Get relative time string for an activity
   */
  getRelativeTime(createdAt: string): string {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) {
      return 'Just now';
    } else if (diffMin < 60) {
      return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
    } else if (diffHour < 24) {
      return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
    } else if (diffDay < 7) {
      return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  /**
   * Check if activity has a navigable resource
   */
  hasResourceLink(activity: OrganizationActivity): boolean {
    return !!activity.resourceType && !!activity.resourceId;
  }

  /**
   * Navigate to activity resource
   */
  navigateToResource(activity: OrganizationActivity): void {
    const route = getResourceRoute(activity.resourceType, activity.resourceId);
    if (route) {
      this.router.navigate([route]);
    }
  }

  /**
   * Check if activity is newly added (for animation)
   */
  isNewActivity(activityId: string): boolean {
    return this.newActivityIds().has(activityId);
  }

  /**
   * Open the quick schedule modal
   */
  openQuickScheduleModal(): void {
    this.quickScheduleModal.open();
  }

  /**
   * Handle successful flight scheduling
   */
  onFlightScheduled(sprayRecordId: string): void {
    // Refresh activities to show the new flight
    this.dashboardService.refreshActivities();
    // Stats will auto-refresh or can be manually refreshed
    this.dashboardService.refreshStats();
  }

  /**
   * Handle manual refresh of stats
   */
  async refreshStats(): Promise<void> {
    await this.dashboardService.refreshStats();
  }
}
